import React from 'react';
import { Typography } from 'antd';

const { Text } = Typography;

// Single Line Chart Component for Education Center/Department data
interface SingleLineChartProps {
    data: Array<{
        period: string;
        periodDescription: string;
        value: number;
        displayValue: string;
        confirmed: boolean;
        updatedAt: string;
    }>;
    height?: number;
}

const SingleLineChart: React.FC<SingleLineChartProps> = ({ data, height = 300 }) => {
    if (data.length === 0) {
        return (
            <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Text type="secondary">Nėra duomenų grafike</Text>
            </div>
        );
    }

    // Get all unique periods and sort them
    const periods = [...new Set(data.map(d => d.period))].sort();

    // Calculate chart dimensions
    const chartWidth = 400;
    const chartHeight = height - 40; // Leave space for labels
    const padding = { top: 20, right: 20, bottom: 40, left: 60 };

    const plotWidth = chartWidth - padding.left - padding.right;
    const plotHeight = chartHeight - padding.top - padding.bottom;

    // Calculate min and max values
    const values = data.map(d => d.value);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const valueRange = maxValue - minValue;
    const paddingValue = valueRange * 0.1; // 10% padding

    const yMin = Math.max(0, minValue - paddingValue);
    const yMax = maxValue + paddingValue;
    const yRange = yMax - yMin;

    // Helper functions for coordinates
    const getX = (period: string) => {
        const index = periods.indexOf(period);
        return padding.left + (index / (periods.length - 1)) * plotWidth;
    };

    const getY = (value: number) => padding.top + plotHeight - ((value - yMin) / yRange) * plotHeight;

    // Generate SVG path
    const generatePath = () => {
        const sortedData = data.sort((a, b) => (
            periods.indexOf(a.period) - periods.indexOf(b.period)
        ));

        if (sortedData.length === 0) return '';

        const pathData = sortedData.map((point, index) => {
            const x = getX(point.period);
            const y = getY(point.value);
            return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
        }).join(' ');

        return pathData;
    };

    // Generate points
    const generatePoints = () => data.map((point) => {
        const x = getX(point.period);
        const y = getY(point.value);
        return (
            <circle
                key={point.period}
                cx={x}
                cy={y}
                r="4"
                fill="#1890ff"
                stroke="white"
                strokeWidth="2" />
        );
    });

    return (
        <div className="single-line-chart">
            <svg width={chartWidth} height={chartHeight} style={{ border: '1px solid #f0f0f0' }}>
                {/* Grid lines */}
                {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
                    const y = padding.top + plotHeight - (ratio * plotHeight);
                    const value = yMin + (ratio * yRange);
                    return (
                        <g key={`grid-${ratio}`}>
                            <line
                                x1={padding.left}
                                y1={y}
                                x2={padding.left + plotWidth}
                                y2={y}
                                stroke="#f0f0f0"
                                strokeWidth="1" />
                            <text
                                x={padding.left - 10}
                                y={y + 4}
                                textAnchor="end"
                                fontSize="12"
                                fill="#666">
                                {value.toFixed(1)}
                            </text>
                        </g>
                    );
                })}

                {/* Period labels */}
                {periods.map((period) => {
                    const x = getX(period);
                    return (
                        <text
                            key={period}
                            x={x}
                            y={chartHeight - 10}
                            textAnchor="middle"
                            fontSize="10"
                            fill="#666">
                            {period}
                        </text>
                    );
                })}

                {/* Line */}
                <path
                    d={generatePath()}
                    fill="none"
                    stroke="#1890ff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round" />

                {/* Points */}
                {generatePoints()}
            </svg>
        </div>
    );
};

SingleLineChart.defaultProps = {
    height: 300,
};

export default SingleLineChart;
